import {
	applyDocumentLocale,
	createTranslate,
	ENGLISH,
	type Locale,
	loadMessages,
	type Messages,
	rememberLocale,
	storedLocale,
	type Translate,
} from "@khazzin/i18n";
import { UiTextProvider } from "@khazzin/ui/primitives/uiText";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

/**
 * The portal's language (EP10-S02): the one this browser last chose, English
 * first while the other bundle loads, and a switch that mirrors the document.
 */

interface LocaleState {
	locale: Locale;
	t: Translate;
	switchLanguage: () => void;
}

const LocaleContext = createContext<LocaleState | null>(null);

export function useLocale(): LocaleState {
	const state = useContext(LocaleContext);
	if (state === null) {
		throw new Error("useLocale must be used inside <PortalLocaleProvider>");
	}
	return state;
}

export function PortalLocaleProvider({ children }: { children: ReactNode }) {
	const [locale, setLocale] = useState<Locale>(storedLocale);
	const [messages, setMessages] = useState<Messages>(ENGLISH);
	useEffect(() => {
		let live = true;
		void loadMessages(locale).then((loaded) => {
			if (live) {
				setMessages(loaded);
			}
		});
		return () => {
			live = false;
		};
	}, [locale]);
	const t = useMemo(() => createTranslate(locale, messages), [locale, messages]);
	const switchLanguage = useCallback(() => {
		const next: Locale = locale === "ar" ? "en" : "ar";
		rememberLocale(next);
		applyDocumentLocale(next);
		setLocale(next);
	}, [locale]);
	const value = useMemo(() => ({ locale, t, switchLanguage }), [locale, t, switchLanguage]);
	// ADJ-M03-1: the design system's own words ("More", "Search") follow the locale.
	return (
		<LocaleContext.Provider value={value}>
			<UiTextProvider t={value.t}>{children}</UiTextProvider>
		</LocaleContext.Provider>
	);
}
